import { useEffect, useState } from "react";
import { exclamationIcon } from "../../assets/icons/SvgIconList";
import StatusChip from "../../components/purchase/StatusChip";
import ValueChange from "../../components/purchase/ValueChange";
import {
  FormRightModalWrapper,
  ICButton,
  Loader,
} from "../../core-component/ui";
import UseEventCallbackHook from "../../hooks/UseEventCallbackHook";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { CURRENCY_SIGN } from "../../utils/AppConstants";
import { prepareMessageFromParams } from "../../utils/AppFunctions";
import { formatRupees } from "../../utils/PriceCalculator";
import {
  CREDIT_TYPE_LABEL_KEY,
  GATEWAY_PAYMENT_CHIP_CLASS,
  ORDER_CONFIRMED_BY_LABEL_KEY,
  ORDER_STATUS_CHIP_CLASS,
  ORDER_STATUS_LABEL_KEY,
  WEBHOOK_EVENT_CHIP_CLASS,
} from "../../utils/PurchaseConstants";
import {
  DOCUMENT_TYPE_ENUM,
  GATEWAY_PAYMENT_STATUS_ENUM,
  ORDER_DISPLAY_STATUS_ENUM,
  ORDER_HOLD_REASON_ENUM,
  PAYMENT_ALERT_TYPE_ENUM,
} from "../../utils/PurchaseEnumerations";
import {
  formatAmountWithSign,
  formatCredits,
  formatPurchaseDateTime,
} from "../../utils/PurchaseFormat";
import UseOrderDocumentDownloadHook from "../../hooks/UseOrderDocumentDownloadHook";
import {
  fetchPaymentOrderDetail,
  getMethodLabel,
  IOrderPayment,
  IPaymentOrderDetail,
} from "./PaymentTransactionViewController";

interface IPaymentOrderDetailProps {
  orderId: string;
  handleClose: () => void;
}

interface IDetailRowProps {
  label: string;
  value: string | null;
}

interface IDetailCardProps {
  title: string;
  children: React.ReactNode;
}

const ALERT_LABEL_KEY: Record<PAYMENT_ALERT_TYPE_ENUM, string> = {
  [PAYMENT_ALERT_TYPE_ENUM.DuplicatePayment]: "alert_duplicate_payment",
  [PAYMENT_ALERT_TYPE_ENUM.AmountMismatch]: "alert_amount_mismatch",
  [PAYMENT_ALERT_TYPE_ENUM.UnknownOrder]: "alert_unknown_order",
  [PAYMENT_ALERT_TYPE_ENUM.LateCapture]: "alert_late_capture",
  [PAYMENT_ALERT_TYPE_ENUM.RefundEvent]: "alert_refund_event",
  [PAYMENT_ALERT_TYPE_ENUM.DisputeEvent]: "alert_dispute_event",
  [PAYMENT_ALERT_TYPE_ENUM.WebhookFailed]: "alert_webhook_failed",
  [PAYMENT_ALERT_TYPE_ENUM.CreditsWhileUnlimited]:
    "alert_credits_while_unlimited",
};

const describeMethod = (
  t: (key: string) => string,
  payment: IOrderPayment,
): string => {
  const method = getMethodLabel(t, payment.method);
  if (payment.card_last4) {
    return `${method} (${[
      payment.card_network,
      payment.card_type,
      payment.card_issuer,
    ]
      .filter(Boolean)
      .join(" ")} •••• ${payment.card_last4})`;
  }
  const detail = payment.bank ?? payment.wallet;
  return detail ? `${method} (${detail})` : method;
};

const DetailRow: React.FC<IDetailRowProps> = ({ label, value }) => (
  <div className="flex justify-between gap-2 text-sm">
    <span className="text-muted">{label}</span>
    <span className="text-right break-all">{value || "-"}</span>
  </div>
);

const DetailCard: React.FC<IDetailCardProps> = ({ title, children }) => (
  <div className="bg-base rounded-md p-3 space-y-1">
    <div className="font-semibold mb-1">{title}</div>
    {children}
  </div>
);

const PaymentOrderDetail: React.FC<IPaymentOrderDetailProps> = ({
  orderId,
  handleClose,
}) => {
  const { t } = UseTranslationHook();
  const { download, isBusy } = UseOrderDocumentDownloadHook();
  const [detail, setDetail] = useState<IPaymentOrderDetail>();
  const [loading, setLoading] = useState(true);
  const close = UseEventCallbackHook(handleClose);

  useEffect(() => {
    fetchPaymentOrderDetail(orderId).then((result) => {
      setDetail(result);
      setLoading(false);
      if (!result) close();
    });
  }, [orderId, close]);

  const renderBody = (data: IPaymentOrderDetail) => {
    const { order } = data;
    const isPaid = order.display_status === ORDER_DISPLAY_STATUS_ENUM.Paid;
    const isDuplicate =
      order.display_status === ORDER_DISPLAY_STATUS_ENUM.Duplicate;
    const refundPayment =
      data.payments.find((payment) => payment.id === order.paid_payment_id) ??
      data.payments.find(
        (payment) => payment.status === GATEWAY_PAYMENT_STATUS_ENUM.Captured,
      );
    const lastAlert = [...data.alerts]
      .reverse()
      .find(
        (alert) =>
          alert.alert_type === PAYMENT_ALERT_TYPE_ENUM.DuplicatePayment &&
          !!alert.sent_at,
      );

    return (
      <div className="space-y-4 pb-4">
        {isDuplicate && (
          <div className="rounded-md p-3 bg-warning-muted flex gap-2 text-sm">
            <span className="size-5 shrink-0 text-warning">
              {exclamationIcon}
            </span>
            <div>
              <b>
                {prepareMessageFromParams(t("refund_needed"), [
                  ["currency", CURRENCY_SIGN],
                  ["amount", formatRupees(order.total_amount)],
                ])}
              </b>{" "}
              {prepareMessageFromParams(t("refund_needed_detail"), [
                ["payment", refundPayment?.gateway_payment_id ?? "-"],
              ])}
              {lastAlert &&
                ` ${prepareMessageFromParams(t("alert_emailed_at"), [
                  ["date", formatPurchaseDateTime(lastAlert.sent_at)],
                ])}`}
            </div>
          </div>
        )}
        {order.hold_reason === ORDER_HOLD_REASON_ENUM.AmountMismatch && (
          <div className="rounded-md p-3 bg-danger-muted text-danger text-sm">
            {t("order_on_hold_amount")}
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <DetailCard title={t("order")}>
            <DetailRow label={t("item")} value={order.item_name} />
            {order.plan_type ? (
              <DetailRow label={t("plan_type")} value={order.plan_type} />
            ) : (
              <DetailRow
                label={t("credits")}
                value={
                  order.credit_type
                    ? `${formatCredits(t, order.credits)} ${t(CREDIT_TYPE_LABEL_KEY[order.credit_type])}`
                    : null
                }
              />
            )}
            <DetailRow label={t("firm_name")} value={order.firm_name} />
            <DetailRow label={t("ordered_by")} value={order.created_by_name} />
            <DetailRow
              label={t("razorpay_order")}
              value={order.gateway_order_id}
            />
            <DetailRow
              label={t("business_information")}
              value={
                order.business_information_version_no
                  ? prepareMessageFromParams(t("version_no"), [
                      [
                        "version",
                        String(order.business_information_version_no),
                      ],
                    ])
                  : null
              }
            />
            <DetailRow label={t("invoice_no")} value={order.invoice_number} />
            <DetailRow label={t("receipt_no")} value={order.receipt_number} />
            <DetailRow
              label={t("confirmed_by")}
              value={
                order.confirmed_by
                  ? t(ORDER_CONFIRMED_BY_LABEL_KEY[order.confirmed_by])
                  : null
              }
            />
          </DetailCard>
          <DetailCard title={t("billing")}>
            <DetailRow label={t("name")} value={order.billing_name} />
            <DetailRow label={t("state")} value={order.billing_state_name} />
            <DetailRow label={t("gstin")} value={order.billing_gstin} />
            <DetailRow label={t("city")} value={order.billing_city} />
            <DetailRow label={t("address")} value={order.billing_address} />
          </DetailCard>
          <DetailCard title={t("amount")}>
            <DetailRow
              label={t("base_price")}
              value={formatAmountWithSign(order.base_amount)}
            />
            {Number(order.igst_amount) > 0 ? (
              <DetailRow
                label={`${t("igst")} ${Number(order.igst_percent)}%`}
                value={formatAmountWithSign(order.igst_amount)}
              />
            ) : (
              <>
                <DetailRow
                  label={`${t("cgst")} ${Number(order.cgst_percent)}%`}
                  value={formatAmountWithSign(order.cgst_amount)}
                />
                <DetailRow
                  label={`${t("sgst")} ${Number(order.sgst_percent)}%`}
                  value={formatAmountWithSign(order.sgst_amount)}
                />
              </>
            )}
            <div className="flex justify-between font-semibold border-t pt-1">
              <span>{t("total")}</span>
              <span>{formatAmountWithSign(order.total_amount)}</span>
            </div>
          </DetailCard>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <DetailCard title={t("status_timeline")}>
            <div className="space-y-2 text-sm">
              {data.timeline.map((row) => (
                <div key={row.id} className="flex gap-2">
                  <span className="mt-1.5 size-2 rounded-full bg-success-emphasis shrink-0" />
                  <div className="flex-1">
                    <div>
                      {row.from_status ? (
                        <ValueChange
                          from={row.from_status}
                          to={row.to_status}
                        />
                      ) : (
                        row.to_status
                      )}
                      <span className="text-muted"> ({row.source})</span>
                    </div>
                    {row.note && <div className="text-muted">{row.note}</div>}
                  </div>
                  <span className="text-muted whitespace-nowrap">
                    {formatPurchaseDateTime(row.created_date)}
                  </span>
                </div>
              ))}
            </div>
          </DetailCard>
          <div className="lg:col-span-2 space-y-3">
            <DetailCard title={t("payment_attempts")}>
              {data.payments.length ? (
                <div className="space-y-2 text-sm">
                  {data.payments.map((payment) => (
                    <div
                      key={payment.id}
                      className="grid grid-cols-5 gap-2 border-b pb-1"
                    >
                      <span className="col-span-2 break-all">
                        {payment.gateway_payment_id}
                      </span>
                      <span>
                        <StatusChip
                          label={payment.status}
                          className={GATEWAY_PAYMENT_CHIP_CLASS[payment.status]}
                        />
                      </span>
                      <span>{describeMethod(t, payment)}</span>
                      <span className="text-muted">
                        {payment.error_description ??
                          payment.error_reason ??
                          "-"}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted">
                  {t("no_payment_attempts")}
                </div>
              )}
            </DetailCard>
            <DetailCard title={t("razorpay_messages")}>
              {data.webhook_events.length ? (
                <div className="space-y-2 text-sm">
                  {data.webhook_events.map((event) => (
                    <div
                      key={event.id}
                      className="grid grid-cols-4 gap-2 border-b pb-1"
                    >
                      <span className="break-all">{event.event_id}</span>
                      <span>{event.event_type}</span>
                      <span className="text-muted">
                        {formatPurchaseDateTime(event.received_at)}
                      </span>
                      <span title={event.last_error ?? ""}>
                        <StatusChip
                          label={event.status}
                          className={WEBHOOK_EVENT_CHIP_CLASS[event.status]}
                        />
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted">
                  {t("no_razorpay_messages")}
                </div>
              )}
            </DetailCard>
            {data.alerts.length > 0 && (
              <DetailCard title={t("admin_alerts")}>
                <div className="space-y-1 text-sm">
                  {data.alerts.map((alert) => (
                    <div key={alert.id} className="flex justify-between gap-2">
                      <span>
                        {t(
                          ALERT_LABEL_KEY[alert.alert_type] ?? alert.alert_type,
                        )}
                      </span>
                      <span className="text-muted">
                        {alert.sent_at
                          ? formatPurchaseDateTime(alert.sent_at)
                          : prepareMessageFromParams(t("alert_not_sent"), [
                              ["attempts", String(alert.attempts)],
                            ])}
                      </span>
                    </div>
                  ))}
                </div>
              </DetailCard>
            )}
          </div>
        </div>
        {isPaid && (
          <div className="flex gap-3">
            <ICButton
              type="button"
              className="!w-fit"
              loading={isBusy(order.id, DOCUMENT_TYPE_ENUM.Invoice)}
              onClick={() => download(order.id, DOCUMENT_TYPE_ENUM.Invoice)}
            >
              {t("download_invoice")}
            </ICButton>
            <ICButton
              type="button"
              className="!w-fit"
              loading={isBusy(order.id, DOCUMENT_TYPE_ENUM.Receipt)}
              onClick={() => download(order.id, DOCUMENT_TYPE_ENUM.Receipt)}
            >
              {t("download_receipt")}
            </ICButton>
          </div>
        )}
      </div>
    );
  };

  const status = detail?.order.display_status;

  return (
    <FormRightModalWrapper
      containerClassName="!w-2/3"
      onClose={handleClose}
      title={t("order_details")}
    >
      {loading || !detail ? (
        <Loader />
      ) : (
        <div>
          {status && (
            <div className="mb-3">
              <StatusChip
                label={t(ORDER_STATUS_LABEL_KEY[status])}
                className={ORDER_STATUS_CHIP_CLASS[status]}
              />
            </div>
          )}
          {renderBody(detail)}
        </div>
      )}
    </FormRightModalWrapper>
  );
};

export default PaymentOrderDetail;
